/**
 * A shared "open this in the app" URL grammar — the pure heart of docs/163-PERMALINKS-
 * AND-DEEP-LINKS.md. One grammar, two carriers: a custom-scheme link (`<scheme>://<path>?
 * <params>`, works today with no hosting — v0) and a universal/app link
 * (`https://<host>/l/<path>?<params>`, v1, needs the hosted AASA/assetlinks + config —
 * `parsePermalink` already accepts this shape so the inbound router is ready for it).
 * `parsePermalink` never throws — an unrecognized path, a missing/invalid required param,
 * or a string that's neither carrier all resolve to `{ kind: "unknown", raw }` (the
 * `Result`-flavored "expected failure" convention this repo uses everywhere else). The
 * model for this file is `content/catalogReference.ts`'s existing partial catalog
 * deep-link parser (`<scheme>://catalog?url=…`), which this grammar subsumes, and 065-
 * SHARING's planned `buildShareUrl`/`parseShareUrl` (065 is meant to sit on top of this
 * layer, not duplicate it — see docs/163 §"Other permalink targets").
 */

export type PermalinkTarget =
  // Add-or-view a catalog (docs/163 headline, P0) — `url` for "add if missing, else view",
  // `catalogId` for "view this already-known catalog by its canonical id".
  | { kind: "catalog"; url?: string; catalogId?: string }
  | { kind: "pack"; packId: string; manifestUrl?: string }
  | { kind: "lesson"; packId: string; lessonId: string; manifestUrl?: string; sectionSlug?: string }
  | { kind: "bible"; book: string; chapter?: number; verse?: number; bibleUrl?: string }
  | { kind: "glossary"; packId: string; term: string }
  | { kind: "space"; spaceId?: string; manifestUrl?: string }
  | { kind: "provider"; providerUrl: string }
  // In-app device-pairing approval (docs/321-IN-APP-DEVICE-APPROVAL.md v0) —
  // `url` identifies WHICH provider minted the code (so the app can route to the
  // right one when several are signed in); `code` is the short-lived, public
  // RFC 8628 `user_code` — carries no secret, so a forged/leaked link can approve
  // nothing without the recipient's own session + an explicit tap.
  | { kind: "device"; url: string; code?: string }
  | { kind: "redeem"; code: string }
  | { kind: "resume" }
  | { kind: "page"; route: string }
  // Degrade target — never throws. `raw` is the original input, for logging/diagnostics
  // only; never open/fetch it.
  | { kind: "unknown"; raw: string };

export interface BuildPermalinkOptions {
  /** Which carrier to emit — `"scheme"` (v0, works with no hosting) or `"https"` (v1,
   * universal link — needs `host`). */
  carrier: "scheme" | "https";
  /** The app's configured custom scheme (e.g. `"beequipped"`/`"lectern"`, from
   * `app.config.ts`). Required for the `"scheme"` carrier. */
  scheme: string;
  /** The hosted domain (e.g. `"getbeequipped.com"`). Required for the `"https"` carrier. */
  host?: string;
}

function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

/** `<scheme>://<path>?<query>` — any non-http(s) scheme (our own app's custom scheme is
 * opaque to this parser, so it accepts any). Group 1 = scheme, 2 = path, 3 = query. */
const CUSTOM_SCHEME_RE = /^([a-z][a-z0-9+.-]*):\/\/([^?]*)(?:\?(.*))?$/i;

function stripSlashes(path: string): string {
  return path.replace(/^\/+|\/+$/g, "");
}

/**
 * The bare grammar path segment (e.g. `"catalog"`) a URL carries, if it matches either
 * carrier shape at all — regardless of whether its params are valid. Exported so a caller
 * (the inbound router) can distinguish "this attempted our `catalog` permalink but with
 * bad/missing params" (degrade to home) from "this is some unrelated custom-scheme link,
 * not one of ours" (leave untouched) — `parsePermalink` alone can't make that distinction
 * since both cases return `{ kind: "unknown" }`.
 */
export function permalinkPathOf(url: string): string | null {
  return extractPathAndQuery(url)?.path ?? null;
}

function extractPathAndQuery(raw: string): { path: string; params: URLSearchParams } | null {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return null;
  const schemeMatch = CUSTOM_SCHEME_RE.exec(trimmed);
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase();
    if (scheme !== "http" && scheme !== "https") {
      return { path: stripSlashes(schemeMatch[2]), params: new URLSearchParams(schemeMatch[3] ?? "") };
    }
  }
  // Universal/app-link carrier (v1): https://<host>/l/<path>?<query>. The `/l/` prefix
  // namespaces app-openable links so a plain `getbeequipped.com/terms` (106) is never
  // mistaken for a permalink.
  try {
    const u = new URL(trimmed);
    if ((u.protocol === "http:" || u.protocol === "https:") && u.pathname.startsWith("/l/")) {
      return { path: stripSlashes(u.pathname.slice(3)), params: u.searchParams };
    }
  } catch {
    // Not a URL at all — falls through to the `null` below.
  }
  return null;
}

/** Accepts either carrier; never throws. See the module doc comment for the degrade rule. */
export function parsePermalink(url: string): PermalinkTarget {
  const extracted = extractPathAndQuery(url);
  if (!extracted) return { kind: "unknown", raw: url };
  const { path, params } = extracted;
  const unknown = (): PermalinkTarget => ({ kind: "unknown", raw: url });

  switch (path) {
    case "catalog": {
      const catalogUrl = params.get("url");
      if (catalogUrl && isHttpUrl(catalogUrl)) return { kind: "catalog", url: catalogUrl };
      const id = params.get("id");
      if (id) return { kind: "catalog", catalogId: id };
      return unknown();
    }
    case "pack": {
      const packId = params.get("packId");
      if (!packId) return unknown();
      const src = params.get("src");
      return { kind: "pack", packId, ...(src && isHttpUrl(src) ? { manifestUrl: src } : {}) };
    }
    case "lesson": {
      const packId = params.get("packId");
      const lessonId = params.get("lessonId");
      if (!packId || !lessonId) return unknown();
      const src = params.get("src");
      const sectionSlug = params.get("s");
      return {
        kind: "lesson",
        packId,
        lessonId,
        ...(src && isHttpUrl(src) ? { manifestUrl: src } : {}),
        ...(sectionSlug ? { sectionSlug } : {}),
      };
    }
    case "bible": {
      const book = params.get("book");
      if (!book) return unknown();
      const chRaw = params.get("ch");
      const chapter = chRaw && /^\d+$/.test(chRaw) ? Number(chRaw) : undefined;
      const vRaw = params.get("v");
      const verse = vRaw && /^\d+$/.test(vRaw) ? Number(vRaw) : undefined;
      const src = params.get("src");
      return {
        kind: "bible",
        book,
        ...(chapter !== undefined ? { chapter } : {}),
        ...(verse !== undefined ? { verse } : {}),
        ...(src && isHttpUrl(src) ? { bibleUrl: src } : {}),
      };
    }
    case "glossary": {
      const packId = params.get("packId");
      const term = params.get("term");
      if (!packId || !term) return unknown();
      return { kind: "glossary", packId, term };
    }
    case "space": {
      const id = params.get("id");
      if (id) return { kind: "space", spaceId: id };
      const manifest = params.get("url");
      if (manifest && isHttpUrl(manifest)) return { kind: "space", manifestUrl: manifest };
      return unknown();
    }
    case "provider": {
      const providerUrl = params.get("url");
      if (!providerUrl || !isHttpUrl(providerUrl)) return unknown();
      return { kind: "provider", providerUrl };
    }
    case "device": {
      const url = params.get("url");
      if (!url || !isHttpUrl(url)) return unknown();
      const code = params.get("code");
      return { kind: "device", url, ...(code ? { code } : {}) };
    }
    case "redeem": {
      const code = params.get("code");
      if (!code) return unknown();
      return { kind: "redeem", code };
    }
    case "resume":
      return { kind: "resume" };
    case "page": {
      // Not yet wired to a navigator in v0 — when it is, the opener must still enforce an
      // allowlist of real routes (docs/163 security rule 3); this only checks the shape.
      const route = params.get("route");
      if (!route || !route.startsWith("/")) return unknown();
      return { kind: "page", route };
    }
    default:
      return unknown();
  }
}

function pathAndParamsFor(target: Exclude<PermalinkTarget, { kind: "unknown" }>): {
  path: string;
  params: URLSearchParams;
} {
  const params = new URLSearchParams();
  switch (target.kind) {
    case "catalog":
      if (target.url) params.set("url", target.url);
      else if (target.catalogId) params.set("id", target.catalogId);
      return { path: "catalog", params };
    case "pack":
      params.set("packId", target.packId);
      if (target.manifestUrl) params.set("src", target.manifestUrl);
      return { path: "pack", params };
    case "lesson":
      params.set("packId", target.packId);
      params.set("lessonId", target.lessonId);
      if (target.manifestUrl) params.set("src", target.manifestUrl);
      if (target.sectionSlug) params.set("s", target.sectionSlug);
      return { path: "lesson", params };
    case "bible":
      params.set("book", target.book);
      if (target.chapter !== undefined) params.set("ch", String(target.chapter));
      if (target.verse !== undefined) params.set("v", String(target.verse));
      if (target.bibleUrl) params.set("src", target.bibleUrl);
      return { path: "bible", params };
    case "glossary":
      params.set("packId", target.packId);
      params.set("term", target.term);
      return { path: "glossary", params };
    case "space":
      if (target.spaceId) params.set("id", target.spaceId);
      else if (target.manifestUrl) params.set("url", target.manifestUrl);
      return { path: "space", params };
    case "provider":
      params.set("url", target.providerUrl);
      return { path: "provider", params };
    case "device":
      params.set("url", target.url);
      if (target.code) params.set("code", target.code);
      return { path: "device", params };
    case "redeem":
      params.set("code", target.code);
      return { path: "redeem", params };
    case "resume":
      return { path: "resume", params };
    case "page":
      params.set("route", target.route);
      return { path: "page", params };
  }
}

/**
 * Builds either carrier for a `PermalinkTarget`. `parsePermalink(buildPermalink(t, opts))`
 * round-trips to `t` for every non-`unknown` kind — the anti-drift guarantee (see the test
 * file). Building an `unknown` target is a no-op that returns its captured `raw` string
 * verbatim (never throws) since there's nothing else to encode.
 */
export function buildPermalink(target: PermalinkTarget, opts: BuildPermalinkOptions): string {
  if (target.kind === "unknown") return target.raw;
  const { path, params } = pathAndParamsFor(target);
  const query = params.toString();
  const suffix = query ? `?${query}` : "";
  if (opts.carrier === "https") return `https://${opts.host ?? ""}/l/${path}${suffix}`;
  return `${opts.scheme}://${path}${suffix}`;
}

/**
 * Canonicalizes a catalog URL for dedup/identity comparison (docs/163's add-or-view flow,
 * and `CatalogStore.add`'s own naïve-`includes` dedup gap): lowercases scheme + host, strips
 * a trailing `/` and a default port, and otherwise leaves the path/query/hash verbatim — a
 * catalog URL may legitimately carry a meaningful query param (a token/version), so it's
 * kept, not stripped (docs/163 "Risks/open questions" recommendation). A value that isn't a
 * parseable URL at all is returned merely trimmed, so two identical garbage strings still
 * compare equal without throwing.
 */
export function normalizeCatalogUrl(url: string): string {
  const trimmed = (url ?? "").trim();
  try {
    const u = new URL(trimmed);
    const scheme = u.protocol.toLowerCase();
    const host = u.hostname.toLowerCase();
    const defaultPort = scheme === "https:" ? "443" : scheme === "http:" ? "80" : "";
    const port = u.port && u.port !== defaultPort ? `:${u.port}` : "";
    const pathname = u.pathname.replace(/\/+$/, "");
    return `${scheme}//${host}${port}${pathname}${u.search}${u.hash}`;
  } catch {
    return trimmed;
  }
}

/**
 * Display-only host label for docs/240-PERMALINK-ADD-OR-VIEW-UX.md Half B's "Add this
 * catalog?" confirmation card — extracts just the hostname (e.g. `getbeequipped.com`) so
 * the emphasized headline reads as a source, not a raw URL. Degrades to the trimmed input
 * string for anything that isn't a parseable URL (never throws), so a pasted/QR-scanned
 * non-URL string still shows *something* rather than a blank headline. Never used for any
 * security decision — the full URL is always shown too (see the card itself).
 */
export function catalogHostLabel(url: string): string {
  const trimmed = (url ?? "").trim();
  try {
    return new URL(trimmed).hostname || trimmed;
  } catch {
    return trimmed;
  }
}
